import React from 'react';
import { Link } from 'react-router-dom';
import { PLANS, COMPARISON, FAQ } from './plans';

const Check = () => (
  <svg className="h-5 w-5 text-success inline" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const Dash = () => <span className="text-gray-600" aria-hidden="true">—</span>;

const Cell = ({ v }) => {
  if (v === true) return <><Check /><span className="sr-only">Included</span></>;
  if (v === false) return <><Dash /><span className="sr-only">Not included</span></>;
  return <span className="text-gray-200 font-medium">{v}</span>;
};

const PlanCard = ({ p }) => (
  <div
    className={`sm-card-static p-7 flex flex-col ${
      p.highlighted ? 'ring-2 ring-blue-500/70 relative' : ''
    }`}
  >
    {p.highlighted && (
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full sm-gradient px-3 py-1 text-xs font-semibold text-white whitespace-nowrap">
        {p.badge}
      </span>
    )}
    <h2 className="text-xl font-semibold text-white">{p.name}</h2>
    <div className="mt-3 flex flex-wrap items-baseline gap-1.5">
      <span className="text-3xl font-bold text-white">{p.price}</span>
      <span className="text-sm text-gray-500">{p.period}</span>
    </div>
    <p className="mt-3 text-sm text-gray-400 min-h-[44px]">{p.tagline}</p>

    {p.ctaHref ? (
      <a
        href={p.ctaHref}
        className={`mt-6 text-sm justify-center ${p.highlighted ? 'sm-primary-button' : 'sm-secondary-button'}`}
      >
        {p.cta}
      </a>
    ) : (
      <Link
        to={p.ctaTo}
        className={`mt-6 text-sm justify-center ${p.highlighted ? 'sm-primary-button' : 'sm-secondary-button'}`}
      >
        {p.cta}
      </Link>
    )}

    <div className="mt-6 flex items-center gap-3 text-xs text-gray-400">
      <span className="rounded-md bg-gray-800 px-2.5 py-1">{p.limits.users}</span>
      <span className="rounded-md bg-gray-800 px-2.5 py-1">{p.limits.widgets}</span>
    </div>

    <ul className="mt-5 space-y-2.5">
      {p.features.map((f) => (
        <li key={f} className="flex items-start gap-2.5 text-sm text-gray-300">
          <span className="mt-0.5 shrink-0"><Check /></span>
          <span>{f}</span>
        </li>
      ))}
      {p.notIncluded.map((f) => (
        <li key={f} className="flex items-start gap-2.5 text-sm text-gray-500">
          <span className="mt-0.5 shrink-0"><Dash /></span>
          <span className="line-through">{f}</span>
        </li>
      ))}
    </ul>
  </div>
);

const PricingPage = () => {
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">
          Pricing that scales with you
        </h1>
        <p className="mt-4 text-lg text-gray-400">
          Start free, move to Pro at $15/mo, or talk to us about Enterprise. Self-serve checkout
          is coming soon — for now a platform admin activates paid plans. Hosting, database, and
          model-provider usage are billed separately.
        </p>
      </div>

      {/* Plan cards */}
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {PLANS.map((p) => (
          <PlanCard key={p.slug} p={p} />
        ))}
      </div>

      {/* Comparison table */}
      <div className="mt-20">
        <h2 className="text-2xl font-bold text-white text-center mb-8">Compare every feature</h2>
        <div className="sm-card-static relative overflow-x-auto" role="region" aria-label="Plan comparison, scroll horizontally on small screens" tabIndex={0}>
          <table className="w-full min-w-[560px] text-sm">
            <caption className="sr-only">Workspace features and configured plan limits</caption>
            <thead>
              <tr className="border-b border-gray-800">
                <th scope="col" className="text-left font-medium text-gray-400 px-5 py-4">Feature</th>
                <th scope="col" className="text-center font-semibold text-white px-5 py-4">Free</th>
                <th scope="col" className="text-center font-semibold text-white px-5 py-4">
                  Pro
                </th>
                <th scope="col" className="text-center font-semibold text-white px-5 py-4">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row, i) => (
                <tr key={row.label} className={i % 2 ? 'bg-gray-800/30' : ''}>
                  <th scope="row" className="text-left font-normal text-gray-300 px-5 py-3.5">{row.label}</th>
                  <td className="text-center px-5 py-3.5"><Cell v={row.free} /></td>
                  <td className="text-center px-5 py-3.5"><Cell v={row.pro} /></td>
                  <td className="text-center px-5 py-3.5"><Cell v={row.enterprise} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ */}
      <div className="mt-20 max-w-3xl mx-auto">
        <h2 className="text-2xl font-bold text-white text-center mb-8">Frequently asked questions</h2>
        <div className="space-y-4">
          {FAQ.map((item) => (
            <details key={item.q} className="sm-card-static p-5 group">
              <summary className="cursor-pointer list-none flex items-center justify-between text-white font-medium">
                {item.q}
                <span className="ml-4 text-gray-500 transition-transform group-open:rotate-45 text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm text-gray-400 leading-relaxed">{item.a}</p>
            </details>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="mt-20 text-center">
        <h2 className="text-2xl font-bold text-white">Ready to build?</h2>
        <Link
          to="/register"
          className="inline-block mt-6 rounded-xl bg-blue-600 px-8 py-3.5 text-base font-semibold text-white hover:bg-blue-700 transition-colors shadow-sm-glow"
        >
          Get started free
        </Link>
      </div>
    </div>
  );
};

export default PricingPage;
